<template>
  <v-container>
    <v-card class="mx-auto mt-6" max-width="720">
      <v-card-title class="headline">Install ReGov Toolkit</v-card-title>
      <v-card-text>
        <p class="body-2">
          Connect this computer to the ReGov Toolkit so it can send files to the cloud and receive
          the files your office pulls down. You do not need an administrator: press the button, run
          the installer that downloads, and the computer sets itself up.
        </p>

        <template v-if="!issuedCode">
          <v-text-field
            id="setupWkst"
            v-model="wkst"
            label="This computer's workstation number"
            hint="The number your office uses for this computer, from 1 to 999."
            persistent-hint
            type="number"
            min="1"
            :rules="wkstRules"
            dense
            class="mt-4"
            @keyup.enter="start"
          ></v-text-field>

          <v-btn
            id="setupStartBtn"
            color="primary"
            class="mt-4"
            :disabled="!wkstNumber || !county || working"
            :loading="working"
            @click="start"
          >
            Download the toolkit installer
          </v-btn>
          <div v-if="!county" class="caption mt-2 orange--text">
            This page is not being served from a county address, so the installer cannot be named
            for your county. Ask a User Administrator to set this computer up.
          </div>
        </template>

        <template v-else>
          <div v-if="downloading" class="mt-4">
            <v-progress-linear
              :value="progress || 0"
              :indeterminate="progress === null"
              height="6"
            ></v-progress-linear>
            <div class="caption mt-1">
              Downloading{{ progress === null ? '' : ` ${progress}%` }} &mdash; this is a large
              file, so it can take a few minutes.
            </div>
          </div>

          <v-alert v-if="savedAs" id="setupDone" type="success" outlined dense class="mt-4">
            <div class="font-weight-bold">The installer is downloaded.</div>
            <ol class="mt-2">
              <li>Open the file <strong>{{ savedAs }}</strong> from your Downloads folder.</li>
              <li>If Windows asks whether to allow it, choose <strong>Run</strong> or <strong>Yes</strong>.</li>
              <li>Wait for it to finish. The toolkit opens and sets this computer up on its own.</li>
            </ol>
            <div class="caption mt-2">
              Run it within {{ expiresIn }}. After that, come back to this page and start again.
            </div>
          </v-alert>

          <v-alert v-if="downloadError" id="setupError" type="warning" outlined dense class="mt-4">
            {{ downloadError }}
            <div class="caption mt-2">
              You can <a :href="plainInstallerUrl" target="_blank" rel="noopener">download the
              installer the ordinary way</a>, then in the toolkit open
              <strong>Settings &rarr; General &rarr; Set up this workstation</strong> and type:
              <code class="setup-line">{{ setupLine }}</code>
            </div>
            <v-btn small class="mt-2" :disabled="downloading" @click="download">
              Try the download again
            </v-btn>
          </v-alert>

          <v-btn id="setupAgainBtn" text color="primary" class="mt-4" @click="reset">
            Set up a different computer
          </v-btn>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { countySlug } from '../../util/clients/client-config';
import { getWorkstationNumber } from '../../util/workstation-util';
import { TOOLKIT_FEED_URL, downloadToolkitInstaller } from '../../util/toolkit-download';

// The highest workstation number the app accepts anywhere (see InputDialogForWorkstationId), and
// the bound the auth service enforces on a self-service code.
const WKST_MAX = 999;

/**
 * Self-service workstation setup: any signed-in user connects the computer they are at.
 *
 * The same mechanism as the administrator's "Set Up a Workstation" (a one-time code that rides in
 * the installer's filename and is redeemed by the toolkit on first run), minus the choices: the
 * auth service fixes the permissions, the lifetime and how many a person may issue a day, so this
 * page asks only for the workstation number. Nothing here lists or touches anyone else's codes or
 * tokens; that stays on the User Administration screen.
 *
 * The code is kept in this component's data and never committed to Vuex, as on the admin dialog.
 */
export default {
  name: 'SetupThisComputer',
  data: () => ({
    // This browser's own workstation number, when it has one, so the common case is one click.
    wkst: getWorkstationNumber() || '',
    working: false,
    issuedCode: '',
    expiresAt: null,
    downloading: false,
    progress: 0,
    savedAs: '',
    downloadError: '',
    wkstRules: [
      (v) => !v || (Number.isInteger(Number(v)) && Number(v) > 0 && Number(v) <= WKST_MAX)
        || `Enter a whole number from 1 to ${WKST_MAX}.`,
    ],
  }),
  computed: {
    county() {
      return countySlug();
    },
    wkstNumber() {
      const num = Number(String(this.wkst).trim());
      return Number.isInteger(num) && num > 0 && num <= WKST_MAX ? num : null;
    },
    plainInstallerUrl() {
      return `${TOOLKIT_FEED_URL}/latest.yml`;
    },
    setupLine() {
      return this.county ? `${this.county}-${this.issuedCode}` : this.issuedCode;
    },
    expiresIn() {
      if (!this.expiresAt) return 'an hour';
      const minutes = Math.round((new Date(this.expiresAt) - Date.now()) / 60000);
      if (minutes <= 1) return 'a minute';
      return minutes >= 60 ? 'an hour' : `${minutes} minutes`;
    },
  },
  methods: {
    async start() {
      if (!this.wkstNumber || this.working) return;
      this.working = true;
      try {
        const created = await this.$store.dispatch('UserAdministration/createMyWorkstationSetup', {
          wkst: this.wkstNumber,
        });
        if (created && created.code) {
          this.issuedCode = created.code;
          this.expiresAt = created.expiresAt;
          await this.download();
        }
      } finally {
        this.working = false;
      }
    },
    async download() {
      this.downloading = true;
      this.downloadError = '';
      this.savedAs = '';
      this.progress = 0;
      try {
        const result = await downloadToolkitInstaller({
          county: this.county,
          code: this.issuedCode,
          onProgress: (percent) => { this.progress = percent; },
        });
        this.savedAs = result.filename;
      } catch (e) {
        this.downloadError = `The installer could not be downloaded from here. ${e.message || ''}`.trim();
        this.$store.dispatch('logException', e, { root: true });
      } finally {
        this.downloading = false;
      }
    },
    reset() {
      this.issuedCode = '';
      this.expiresAt = null;
      this.downloading = false;
      this.progress = 0;
      this.savedAs = '';
      this.downloadError = '';
    },
  },
};
</script>

<style scoped>
.setup-line {
  font-family: monospace;
}
</style>
